import { useState } from 'react';
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import { useUpdateSection, useCreateLesson } from '@/api/course-builder';
import { LessonFormDialog } from '@/components/forms/lesson-form';
import { QuizBuilderDialog } from '@/components/forms/quiz-builder';
import { LessonListItem } from './lesson-list-item';
import { AddLessonInline } from './add-lesson-inline';
import type { BuilderSection, BuilderLesson, LessonType } from '@/types';

type SectionItemProps = {
  section: BuilderSection;
  isDraft: boolean;
  isExpanded: boolean;
  courseId: number;
  onToggle: () => void;
  onDelete: (section: BuilderSection) => void;
  onDeleteLesson: (lesson: BuilderLesson, sectionId: number) => void;
};

export function SectionItem({
  section,
  isDraft,
  isExpanded,
  courseId,
  onToggle,
  onDelete,
  onDeleteLesson,
}: SectionItemProps) {
  const updateSectionMut = useUpdateSection();
  const createLessonMut = useCreateLesson();

  const [editingSectionId, setEditingSectionId] = useState<number | null>(null);
  const [editingSectionTitle, setEditingSectionTitle] = useState('');
  const [addingLesson, setAddingLesson] = useState(false);
  const [newLessonTitle, setNewLessonTitle] = useState('');
  const [newLessonType, setNewLessonType] = useState<LessonType>('content');
  const [editingLesson, setEditingLesson] = useState<BuilderLesson | null>(null);
  const [quizLesson, setQuizLesson] = useState<BuilderLesson | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  const lessons = [...(section.lessons ?? [])].sort((a, b) => a.sort_order - b.sort_order);

  const handleUpdateSection = () => {
    if (!editingSectionTitle.trim()) return;
    updateSectionMut.mutate(
      { courseId, sectionId: section.id, data: { title: editingSectionTitle.trim() } },
      { onSuccess: () => setEditingSectionId(null) },
    );
  };

  const handleCreateLesson = () => {
    if (!newLessonTitle.trim()) return;
    setCreateError(null);
    createLessonMut.mutate(
      {
        courseId,
        sectionId: section.id,
        data: { title: newLessonTitle.trim(), lesson_type: newLessonType },
      },
      {
        onSuccess: () => {
          setNewLessonTitle('');
          setNewLessonType('content');
          setAddingLesson(false);
          setCreateError(null);
        },
        onError: (err) => {
          setCreateError(err instanceof Error ? err.message : 'Error al crear la lección');
        },
      },
    );
  };

  return (
    <div className="rounded-xl border border-border">
      {/* Section header */}
      <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-4 py-3">
        <button onClick={onToggle} className="text-muted-foreground">
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {isDraft && <GripVertical className="h-4 w-4 text-muted-foreground/50" />}

        {editingSectionId === section.id ? (
          <div className="flex flex-1 items-center gap-2">
            <input
              value={editingSectionTitle}
              onChange={(e) => setEditingSectionTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleUpdateSection()}
              className="flex-1 rounded border border-input bg-background px-2 py-1 text-sm"
              autoFocus
            />
            <button onClick={handleUpdateSection} className="text-xs text-primary">
              Guardar
            </button>
            <button
              onClick={() => setEditingSectionId(null)}
              className="text-xs text-muted-foreground"
            >
              Cancelar
            </button>
          </div>
        ) : (
          <>
            <span className="flex-1 text-sm font-medium text-foreground">{section.title}</span>
            <span className="text-xs text-muted-foreground">{lessons.length} lecciones</span>
            {isDraft && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => {
                    setEditingSectionId(section.id);
                    setEditingSectionTitle(section.title);
                  }}
                  className="rounded p-1 text-muted-foreground hover:bg-accent"
                  aria-label="Editar sección"
                  title="Editar sección"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => onDelete(section)}
                  className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  aria-label="Eliminar sección"
                  title="Eliminar sección"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Lessons */}
      {isExpanded && (
        <div className="divide-y divide-border">
          {lessons.map((lesson) => (
            <LessonListItem
              key={lesson.id}
              lesson={lesson}
              isDraft={isDraft}
              onEdit={setEditingLesson}
              onQuiz={setQuizLesson}
              onDelete={(l) => onDeleteLesson(l, section.id)}
            />
          ))}

          {/* Add lesson inline */}
          {isDraft && addingLesson ? (
            <AddLessonInline
              title={newLessonTitle}
              setTitle={setNewLessonTitle}
              lessonType={newLessonType}
              setLessonType={setNewLessonType}
              isPending={createLessonMut.isPending}
              error={createError}
              onSubmit={handleCreateLesson}
              onCancel={() => {
                setAddingLesson(false);
                setCreateError(null);
              }}
            />
          ) : isDraft ? (
            <button
              onClick={() => setAddingLesson(true)}
              className="flex w-full items-center gap-2 px-4 py-2 pl-12 text-xs text-muted-foreground hover:bg-muted/20 hover:text-foreground"
            >
              <Plus className="h-3.5 w-3.5" /> Agregar lección
            </button>
          ) : null}
        </div>
      )}

      {/* Lesson editor dialog */}
      {editingLesson && (
        <LessonFormDialog
          lesson={editingLesson}
          courseId={courseId}
          onClose={() => setEditingLesson(null)}
        />
      )}

      {/* Quiz builder dialog */}
      {quizLesson && (
        <QuizBuilderDialog
          lessonId={quizLesson.id}
          quizId={quizLesson.quiz?.id ?? null}
          courseId={courseId}
          onClose={() => setQuizLesson(null)}
        />
      )}
    </div>
  );
}
